import { createPromiseClient } from "@connectrpc/connect";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { DataApiService } from "@uniswap/client-data-api/dist/data/v1/api_connect";
import { uniswapGetTransport } from "uniswap/src/data/rest/base";
import { transformInput } from "uniswap/src/data/rest/utils";
import { useEnabledChains } from "uniswap/src/features/chains/hooks/useEnabledChains";
import { useRestPortfolioValueModifier } from "uniswap/src/features/dataApi/balancesRest";
import { currencyIdToAddress, currencyIdToChain, isNativeCurrencyAddress } from "uniswap/src/utils/currencyId";
import { useEvent } from "utilities/src/react/hooks";
import { ReactQueryCacheKey } from "utilities/src/reactQuery/cache";
const portfolioClient = createPromiseClient(DataApiService, uniswapGetTransport);
export function useGetPortfolioQuery(params) {
  return useQuery(getPortfolioQuery(params));
}
export const getPortfolioQuery = ({
  input,
  enabled,
  refetchInterval,
  select
}) => {
  const transformedInput = transformInput(input);
  return queryOptions({
    queryKey: [ReactQueryCacheKey.GetPortfolio, transformedInput],
    queryFn: () => transformedInput ? portfolioClient.getPortfolio(transformedInput) : Promise.resolve(void 0),
    placeholderData: (prev) => prev,
    // this prevents the loading skeleton from appearing when hiding/unhiding tokens
    refetchInterval,
    enabled: enabled ?? void 0,
    select
  });
};
export function useRestTokenBalanceQuantityParts({
  currencyId,
  address
}) {
  const { chains: chainIds } = useEnabledChains();
  const modifier = useRestPortfolioValueModifier(address);
  const selectQuantityParts = useEvent((data) => {
    const balance = _findBalanceFromCurrencyId(data, currencyId);
    return balance ? { quantity: balance.amount?.amount || 0 } : void 0;
  });
  return useQuery({
    ...getPortfolioQuery({ input: { evmAddress: address, chainIds, modifier } }),
    select: selectQuantityParts
  });
}
export function useRestTokenBalanceMainParts({
  currencyId,
  address
}) {
  const { chains: chainIds } = useEnabledChains();
  const modifier = useRestPortfolioValueModifier(address);
  const selectMainParts = useEvent((data) => {
    const balance = _findBalanceFromCurrencyId(data, currencyId);
    return balance ? {
      denominatedValue: { value: balance.valueUsd },
      tokenProjectMarket: {
        relativeChange24: { value: balance.pricePercentChange1d }
      }
    } : void 0;
  });
  return useQuery({
    ...getPortfolioQuery({ input: { evmAddress: address, chainIds, modifier } }),
    select: selectMainParts
  });
}
function _findBalanceFromCurrencyId(data, currencyId) {
  if (!data?.portfolio?.balances || !currencyId) {
    return void 0;
  }
  const tokenAddress = currencyIdToAddress(currencyId);
  const chainId = currencyIdToChain(currencyId);
  const isNative = chainId && isNativeCurrencyAddress(chainId, tokenAddress);
  return data.portfolio.balances.find((bal) => {
    if (bal.token?.chainId !== chainId) {
      return false;
    }
    if (isNative) {
      return isNativeCurrencyAddress(chainId, bal.token.address);
    }
    return bal.token.address === tokenAddress;
  });
}
